import React, { useContext, useMemo } from 'react';
import { round } from 'lodash';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { MeasurementDataProvider } from '../MeasurementForm';
import { specNumRegex, checkIs3DigitDecimal } from '../../../../../Components/helper';
import { isValidGradingInput, GRADING_INPUT_PATTERN } from '../../../../../utils/calculationHelpers';

export default function Gradings({ nestIndex, control, register, setValue, errors, isExistingState, productRefData }) {
  const { findFractionDiff } = useContext(MeasurementDataProvider);

  const { fields: specGradingsArray } = useFieldArray({
    control,
    name: `specPomLibraries[${nestIndex}].gradings`,
  });

  const { clearErrors, origanalMeasurmentData } = useFormContext();

  const findFracDiff = async (e, parentIndex, measureGrad) => {
    const { value = '', name } = e.currentTarget || {};

    if (!name) return; // if somehow name is missing, stop.

    const inputValue = value.trim();

    if (inputValue === '' || inputValue === '0') {
      await setValue(name, 0, { shouldValidate: false });
      return;
    }

    clearErrors(name);
    await setValue(name, inputValue, { shouldValidate: true });
    await findFractionDiff(parentIndex, undefined, measureGrad);
  };

  const sizeRanges = useMemo(() => {
    const sizesArr = [];
    origanalMeasurmentData[0]?.sizeRanges?.forEach((item) => {
      if (productRefData?.sizeRanges?.includes(item)) {
        sizesArr.push(item);
      }
    });
    return sizesArr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origanalMeasurmentData]);

  const handleInputChange = (e, fieldName) => {
    const { value } = e.target;
    setValue(fieldName, value);
  };
  function isNumberOrSpecial(val) {
    if (val === undefined || val === null) return '0';
    if (!Number.isNaN(val) && !Number.isNaN(parseFloat(val))) {
      return round(Number(val), 3);
    }
    return val;
  }

  return specGradingsArray?.map((item, k) => {
    return (
      productRefData &&
      sizeRanges?.indexOf(item.sizeName) > -1 && (
        <td key={k} className="text-center">
          <div className="common-input-control mb-0">
            <input
              type="text"
              step="any"
              onKeyPress={(e) => {
                // For sample size (when isZero is true), allow only numbers and decimal points
                // For other cases, allow numbers, '+', '-', 'N', 'A', 'n', 'a', '/', and decimal point
                const allowed = item?.isZero
                  ? /[0-9.]/ // Only numbers and decimal point for sample size
                  : GRADING_INPUT_PATTERN; // Updated validation to include + sign and lowercase na for non-sample size

                if (!allowed.test(e.key)) {
                  e.preventDefault();
                }
              }}
              className="form-control input-spec-text grading-text"
              name={`specPomLibraries[${nestIndex}].gradings[${k}].measurment`}
              {...register(`specPomLibraries[${nestIndex}].gradings[${k}].measurment`, {
                required: false,
                messsage: 'required',
                validate: {
                  maxFourDigitsBeforeDecimal: (value) => {
                    // For sample size (when isZero is true), only allow numeric values
                    if (item?.isZero) {
                      // For sample size, only validate numeric values
                      if (value === '' || value === '0') {
                        return true;
                      }
                      return specNumRegex.test(value) || 'Max 4 digit allowed before decimal & 3 digits after decimal';
                    }

                    // For non-sample size, allow special values without number validation
                    if (['-', 'N/A', 'NA', 'na', '/', 'N', 'A'].includes(value) || Number.isNaN(value)) {
                      return true;
                    }
                    return specNumRegex.test(value) || 'Max 4 digit allowed before decimal & 3 digits after decimal';
                  },
                },
                onBlur: (e) => findFracDiff(e, nestIndex, 'grading'),
              })}
              defaultValue={item?.measurment ? item?.measurment : 0}
              onChange={(e) => {
                const val = e.target.value;
                if (checkIs3DigitDecimal(val)) {
                  handleInputChange(e, `specPomLibraries[${nestIndex}].gradings[${k}].measurment`);
                }
              }}
              disabled={!item?.isZero}
            />

            {errors?.specPomLibraries?.[nestIndex]?.gradings[k]?.measurment?.message && (
              <small className="error-message text-center ">{errors?.specPomLibraries?.[nestIndex]?.gradings[k]?.measurment?.message}</small>
            )}
            {errors?.specPomLibraries?.[nestIndex]?.gradings[k]?.measurment?.type === 'required' && (
              <small className="error-message text-center ">{errors?.specPomLibraries?.[nestIndex]?.gradings[k]?.measurment?.type}</small>
            )}

            <span className={isExistingState ? 'd-block mt-2' : 'd-none'}>
              <input
                type="text"
                step="any"
                className="form-control input-spec-text grading-text"
                name={`specPomLibraries[${nestIndex}].gradings[${k}].grading`}
                {...register(`specPomLibraries[${nestIndex}].gradings[${k}].grading`, {
                  required: false,
                  message: 'required',
                  onBlur: (e) => findFracDiff(e, nestIndex, 'grading'),
                })}
                defaultValue={isNumberOrSpecial(item?.grading)}
                disabled={item?.isZero}
                onChange={(e) => {
                  const val = e.target.value;
                  // Enhanced validation prevents multiple signs (++, +-, --)
                  if (isValidGradingInput(val)) {
                    handleInputChange(e, `specPomLibraries[${nestIndex}].gradings[${k}].grading`);
                  }
                }}
              />

              {errors?.specPomLibraries?.[nestIndex]?.gradings[k]?.grading?.message && (
                <small className="error-message text-center ">{errors?.specPomLibraries?.[nestIndex]?.gradings[k]?.grading?.message}</small>
              )}
            </span>
          </div>
        </td>
      )
    );
  });
}
